import { zodResolver } from '@hookform/resolvers/zod';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const bookingDetailsFormSchema = (intl) =>
  z.object({
    activity: z.string().min(1, {
      message: intl.formatMessage({
        id: 'booking_details_form.activity_validation',
        defaultMessage: 'Please enter an activity.'
      })
    }),
    totalGuests: z.string().min(1, {
      message: intl.formatMessage({
        id: 'booking_details_form.attendees_validation',
        defaultMessage: 'Please enter the number of attendees.'
      })
    }),
    alcoholConsumed: z.boolean({
      invalid_type_error: intl.formatMessage({
        id: 'booking_details_form.alcohol_option_validation',
        defaultMessage: 'Please select an option.'
      })
    }),
    hostRulesAccepted: z.boolean().refine((val) => val === true, {
      message: intl.formatMessage({
        id: 'booking_details_form.accept_rules',
        defaultMessage: 'Please accept the rules.'
      })
    }),
    message: z
      .string()
      .trim()
      .min(1, {
        message: intl.formatMessage({
          id: 'booking_details_form.enter_message',
          defaultMessage: 'Please enter a message.'
        })
      })
      .refine((val) => val.length >= 6, {
        message: intl.formatMessage(
          {
            id: 'booking_details_form.min_characters',
            defaultMessage: 'Minimum is {count} characters.'
          },
          { count: intl.formatNumber(6) }
        )
      })
      .refine((val) => val.length <= 10000, {
        message: intl.formatMessage(
          {
            id: 'booking_details_form.max_characters',
            defaultMessage: 'Maximum is {count} characters.'
          },
          { count: intl.formatNumber(10000) }
        )
      })
  });

export const useBookingDetailsForm = ({
  onChange,
  defaultValues,
  intl,
  debounceDelay = 1000
}) => {
  const bookingDetailsForm = useForm({
    resolver: zodResolver(bookingDetailsFormSchema(intl)),
    defaultValues: {
      schedule: [],
      activity: '',
      totalGuests: '',
      alcoholConsumed: null,
      message: '',
      hostRulesAccepted: false,
      addOns: null,
      ...(defaultValues || {})
    }
  });

  const prevFormValues = React.useRef(
    cloneDeep(bookingDetailsForm.getValues())
  );
  const getChangedFields = () => {
    const newValues = bookingDetailsForm.getValues();
    const changedFields = Object.keys(newValues).filter(
      (field) => !isEqual(newValues[field], prevFormValues.current[field])
    );

    return changedFields;
  };

  const debouncedOnChange = debounce((newValues) => {
    onChange?.(newValues, getChangedFields(newValues));
    prevFormValues.current = cloneDeep(newValues);
  }, debounceDelay);

  React.useEffect(() => {
    const formSubscription = bookingDetailsForm.watch(debouncedOnChange);
    return () => {
      formSubscription?.unsubscribe();
    };
  }, []);

  // uncomment this and set defaultValues to BOOKING_DETAILS_FORM_DEV_DEFAULTS for faster development (import BOOKING_DETAILS_FORM_DEV_DEFAULTS from checkout-utils.js)
  // React.useEffect(() => {
  //   onChange?.(BOOKING_DETAILS_FORM_DEV_DEFAULTS, 'schedule');
  // }, []);

  return { bookingDetailsForm };
};
